'use client'

import { useCallback, useEffect, useState } from 'react'
import type { PlayerTrack } from './player-context'

/**
 * Historique RÉEL des écoutes : journal append-only (chaque lecture = une
 * entrée horodatée, les répétitions sont conservées). À la différence de
 * "récemment écoutés" qui dédupliquait par morceau.
 *
 * Stocké en localStorage (par appareil). Cap à MAX entrées.
 */
const KEY = 'blc.history.v2'
const MAX = 1000

export type HistoryEntry = PlayerTrack & { playedAt: number }

function readAll(): HistoryEntry[] {
  if (typeof window === 'undefined') return []
  try { const raw = localStorage.getItem(KEY); return raw ? JSON.parse(raw) : [] } catch { return [] }
}

/** Ajoute une écoute à l'historique (anti-doublon si même morceau < 30 s). */
export function logHistory(t: PlayerTrack) {
  if (typeof window === 'undefined' || !t?.videoId) return
  try {
    const arr = readAll()
    const now = Date.now()
    const last = arr[0]
    if (last && last.videoId === t.videoId && now - last.playedAt < 30_000) return
    arr.unshift({
      videoId: t.videoId, title: t.title, artist: t.artist,
      thumbnail: t.thumbnail, playedAt: now,
    })
    if (arr.length > MAX) arr.length = MAX
    localStorage.setItem(KEY, JSON.stringify(arr))
    window.dispatchEvent(new CustomEvent('blc-history'))
  } catch {}
}

export function useHistory() {
  const [history, setHistory] = useState<HistoryEntry[]>([])
  const reload = useCallback(() => setHistory(readAll()), [])
  useEffect(() => {
    reload()
    const h = () => reload()
    window.addEventListener('blc-history', h)
    window.addEventListener('storage', h)
    return () => { window.removeEventListener('blc-history', h); window.removeEventListener('storage', h) }
  }, [reload])
  const clear = useCallback(() => {
    try { localStorage.removeItem(KEY) } catch {}
    setHistory([])
    window.dispatchEvent(new CustomEvent('blc-history'))
  }, [])
  return { history, clear, reload }
}

/** Top artistes de l'historique (pour les recommandations). */
export function topArtistsFromHistory(history: HistoryEntry[], n = 6): string[] {
  const counts = new Map<string, number>()
  for (const h of history) {
    const a = (h.artist || '').replace(/\s*-\s*Topic\s*$/i, '').trim()
    if (!a) continue
    counts.set(a, (counts.get(a) || 0) + 1)
  }
  return [...counts.entries()].sort((a, b) => b[1] - a[1]).slice(0, n).map(([a]) => a)
}
